import React from "react";
import { Link, useLocation } from "react-router-dom";
import { FiChevronDown, FiSearch, FiSmartphone, FiX } from "react-icons/fi";
import { useUser } from "../../hooks";
import FallbackImage from "../Common/ImageWrapper";
import MenuSearch from "../Shell/MenuSearch";
import { openMenuSearch } from "../Shell/shellEvents";
import { buildMenu, isPathMatch } from "../Shell/menuModel";
import { iconFor } from "../Shell/icons";
import "../Shell/shell.css";

const PLAY_STORE_APP_URL = "https://play.google.com/store/apps/details?id=com.unquiecoop";
const APP_DISMISSED_KEY = "sx_app_card_dismissed";
const DRAWER_BREAKPOINT = 1200;

const cleanCompanyName = (name) => (name ? name.trim().replace(/\s+/g, " ") : "");

/** A parent group is "current" when the page is its own path or any of its children. */
function isSubMenuActive(item, pathname) {
  if (isPathMatch(pathname, item?.path)) return true;
  return (item?.subMenu || []).some((sub) => isPathMatch(pathname, sub?.path));
}

/** Prefer the longest matching child so nested routes light up the right entry. */
function getBestMatchingSubPath(subMenu, pathname) {
  const matches = (subMenu || []).map((sub) => sub?.path).filter((path) => isPathMatch(pathname, path));
  return matches.length ? matches.sort((a, b) => b.length - a.length)[0] : null;
}

const EXACT_ONLY = ["/member/dashboard", "/invest/dashboard"];

const readDismissed = () => {
  try {
    return window.localStorage.getItem(APP_DISMISSED_KEY) === "1";
  } catch (e) {
    return false;
  }
};

export const SidebarAdmin = ({ hideSider, setHideSider, sideBarData = [], companyNameOverride, companyLogoOverride }) => {
  const { pathname } = useLocation();
  const user = useUser();
  const [manualOpen, setManualOpen] = React.useState({});
  const [soonOpen, setSoonOpen] = React.useState(false);
  const [appDismissed, setAppDismissed] = React.useState(readDismissed);
  const closeRef = React.useRef(null);

  const companyName = cleanCompanyName(companyNameOverride || user?.company?.name || user?.cooperative_name || "");
  const companyLogo = companyLogoOverride || user?.company?.logo_url || "/Images/logo1.svg";

  const menu = React.useMemo(() => buildMenu(sideBarData), [sideBarData]);

  const closeDrawer = () => {
    if (typeof window !== "undefined" && window.innerWidth < DRAWER_BREAKPOINT) setHideSider?.(false);
  };

  // Phone/tablet drawer: Escape closes it, the page behind does not scroll, focus lands inside.
  React.useEffect(() => {
    if (!hideSider || typeof window === "undefined" || window.innerWidth >= DRAWER_BREAKPOINT) return undefined;
    const onKey = (e) => e.key === "Escape" && setHideSider?.(false);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKey);
    };
  }, [hideSider, setHideSider]);

  const dismissApp = () => {
    setAppDismissed(true);
    try {
      window.localStorage.setItem(APP_DISMISSED_KEY, "1");
    } catch (e) {
      /* storage blocked: it just comes back next visit */
    }
  };

  const topLinkActive = (item) =>
    pathname === item?.path || (!EXACT_ONLY.includes(item?.path) && isPathMatch(pathname, item?.path));

  const renderItem = (item, index) => {
    const Icon = iconFor(item);

    if (!item?.subMenu?.length || item?.dropDown === "none") {
      const active = topLinkActive(item);
      return (
        <li key={`${item.path}-${index}`}>
          <Link to={item.path} className={`sx-link ${active ? "on" : ""}`} aria-current={active ? "page" : undefined} onClick={closeDrawer}>
            <Icon size={18} aria-hidden="true" />
            <span>{item.name}</span>
          </Link>
        </li>
      );
    }

    const sectionActive = isSubMenuActive(item, pathname);
    const key = item.path || item.name || String(index);
    const open = manualOpen[key] ?? sectionActive;
    const bestSubPath = getBestMatchingSubPath(item.subMenu, pathname);
    const panelId = `sx-sub-${index}`;

    return (
      <li key={`${key}-${index}`}>
        <button
          type="button"
          className={`sx-link ${sectionActive ? "has-on" : ""}`}
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setManualOpen((prev) => ({ ...prev, [key]: !open }))}
        >
          <Icon size={18} aria-hidden="true" />
          <span>{item.name}</span>
          <FiChevronDown className="sx-chev" size={16} aria-hidden="true" />
        </button>
        {open ? (
          <ul className="sx-sub" id={panelId}>
            {item.subMenu.map((sub, i) => {
              if (sub?.type === "section") {
                return (
                  <li key={`s-${i}`} className="sx-sublabel">
                    {sub.name}
                  </li>
                );
              }
              if (sub?.disabled || !sub?.path) {
                return (
                  <li key={i}>
                    <span className="sx-sublink off" aria-disabled="true">
                      {sub.name} (soon)
                    </span>
                  </li>
                );
              }
              const active = bestSubPath === sub.path;
              const tour =
                sub.path === "/super_admin/communication/group_management"
                  ? "staff-comm-groups"
                  : sub.path === "/super_admin/communication/summary"
                    ? "staff-comm-summary"
                    : undefined;
              return (
                <li key={i}>
                  <Link to={sub.path} className={`sx-sublink ${active ? "on" : ""}`} aria-current={active ? "page" : undefined} data-tour={tour} onClick={closeDrawer}>
                    {sub.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : null}
      </li>
    );
  };

  return (
    <div className="sx">
      <div className={`sx-backdrop ${hideSider ? "on" : ""}`} onClick={() => setHideSider?.(false)} aria-hidden="true" />
      <aside id="sidebar-main" className={`sx-aside ${hideSider ? "open" : ""}`} data-tour="tour-sidebar">
        <div className="sx-brand">
          <div className="sx-logo">
            <FallbackImage src={companyLogo} alt={companyName || "Company logo"} width="40" height="40" objectFit="contain" fallback="/Images/logo1.svg" />
          </div>
          {companyName ? (
            <h2 className="sx-company" title={companyName}>
              {companyName}
            </h2>
          ) : null}
          <button ref={closeRef} type="button" className="sx-iconbtn sx-close" onClick={() => setHideSider?.(false)} aria-label="Close menu">
            <FiX size={20} aria-hidden="true" />
          </button>
        </div>

        <button type="button" className="sx-search" onClick={openMenuSearch}>
          <FiSearch size={16} aria-hidden="true" />
          <span>Search menu</span>
          <kbd>Ctrl K</kbd>
        </button>

        <nav className="sx-nav" aria-label="Main menu">
          {menu.sections.map((section, s) => (
            <div key={`${section.label}-${s}`}>
              {menu.showLabels && section.label ? <p className="sx-sec">{section.label}</p> : null}
              <ul>{section.items.map(renderItem)}</ul>
            </div>
          ))}

          {menu.soon.length ? (
            <div className="sx-soon">
              <button type="button" className="sx-link muted" aria-expanded={soonOpen} aria-controls="sx-soon-list" onClick={() => setSoonOpen((v) => !v)}>
                <span>Coming soon</span>
                <em className="sx-count" style={{ fontStyle: "normal" }}>{menu.soon.length}</em>
                <FiChevronDown className="sx-chev" size={16} aria-hidden="true" />
              </button>
              {soonOpen ? (
                <ul className="sx-sub" id="sx-soon-list">
                  {menu.soon.map((item, i) => (
                    <li key={i}>
                      <Link to={item.path} className={`sx-sublink ${isPathMatch(pathname, item.path) ? "on" : ""}`} onClick={closeDrawer}>
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </nav>

        {!appDismissed ? (
          <div className="sx-foot">
            <div className="sx-app">
              <a className="sx-app-link" href={PLAY_STORE_APP_URL} target="_blank" rel="noopener noreferrer">
                <FiSmartphone size={16} aria-hidden="true" />
                Get the mobile app
              </a>
              <button type="button" className="sx-iconbtn" style={{ width: 34, height: 34, border: 0 }} onClick={dismissApp} aria-label="Dismiss the mobile app suggestion">
                <FiX size={16} aria-hidden="true" />
              </button>
            </div>
          </div>
        ) : null}
      </aside>
      <MenuSearch items={sideBarData} />
    </div>
  );
};
